import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { generateSEOData } from '@/components/feature/SEOHead';

const seo = generateSEOData({
  title: '关于我们',
  description: '黄游下戟文化研究，致力于中国古代典故的深度解析与文化传播。以严谨的学术态度和生动的表达方式，传承经典文化意象。',
  canonicalUrl: '/about',
});

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qugvnnahb7382ovg8g', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '黄游下戟文化研究',
        url: 'https://huangyouxiaji.com',
        description: '深耕中国古典文化，以严谨的学术态度和生动的表达方式，为您全面解析黄游下戟这一经典典故。',
        foundingDate: '2020',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@huangyouxiaji.com',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebPage',
        name: '关于我们 - 黄游下戟文化研究',
        url: 'https://huangyouxiaji.com/about',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangyouxiaji.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        },
      },
    ],
  };

  const teamMembers = [
    {
      name: '文渊阁',
      role: '主编 / 文献考据',
      description: '古典文献学博士，专注先秦两汉文献研究十余年，对黄游下戟的文献出处有深入的考证。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Chinese%20scholar%20portrait%2C%20middle%20aged%20man%20in%20traditional%20Chinese%20scholar%20robe%2C%20intellectual%20and%20refined%20look%2C%20warm%20lighting%2C%20simple%20background%2C%20professional%20headshot%20style&width=400&height=400&seq=team-1&orientation=squarish',
    },
    {
      name: '史海钩沉',
      role: '历史背景研究员',
      description: '中国古代史专业硕士，擅长将晦涩的历史背景以通俗易懂的方式呈现给读者。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Chinese%20female%20historian%20portrait%2C%20elegant%20and%20scholarly%20appearance%2C%20warm%20natural%20lighting%2C%20simple%20background%2C%20professional%20headshot%20style&width=400&height=400&seq=team-2&orientation=squarish',
    },
    {
      name: '诗海寻踪',
      role: '诗词文学研究员',
      description: '中国古代文学研究者，精研唐诗宋词，致力于发掘典故在文学中的传承与演变。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Chinese%20young%20male%20literature%20researcher%20portrait%2C%20intellectual%20and%20creative%20look%2C%20warm%20natural%20lighting%2C%20simple%20background%2C%20professional%20headshot%20style&width=400&height=400&seq=team-3&orientation=squarish',
    },
    {
      name: '哲思探微',
      role: '文化哲学研究员',
      description: '中国哲学专业背景，从道家和儒家的视角深入解读典故的哲学内涵与当代意义。',
      imageUrl: 'https://readdy.ai/api/search-image?query=Chinese%20female%20philosophy%20researcher%20portrait%2C%20wise%20and%20contemplative%20appearance%2C%20warm%20natural%20lighting%2C%20simple%20background%2C%20professional%20headshot%20style&width=400&height=400&seq=team-4&orientation=squarish',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
              关于我们
            </h1>
            <p className="text-foreground-600 text-base leading-relaxed max-w-2xl mx-auto mb-8">
              黄游下戟文化研究，是一个专注于中国古典文化深度解析的学术型内容平台。
              我们以严谨的学术态度和生动的表达方式，致力于让更多人了解并喜爱中国传统文化中的经典意象。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mx-auto mb-3">
                  <i className="ri-book-read-line text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-foreground-900 mb-1 font-heading">8+</div>
                <div className="text-xs text-foreground-500">深度研究文章</div>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mx-auto mb-3">
                  <i className="ri-team-line text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-foreground-900 mb-1 font-heading">4</div>
                <div className="text-xs text-foreground-500">专业研究员</div>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mx-auto mb-3">
                  <i className="ri-calendar-check-line text-xl"></i>
                </div>
                <div className="text-2xl font-bold text-foreground-900 mb-1 font-heading">2020</div>
                <div className="text-xs text-foreground-500">创立年份</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                研究团队
              </h2>
              <p className="text-foreground-500 text-sm max-w-lg mx-auto">
                汇聚各领域专业力量，为您呈现有深度、有温度的文化内容
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="rounded-xl bg-background-50 border border-background-200/70 p-6 text-center"
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4">
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-primary-600 text-xs font-medium mb-3">{member.role}</p>
                  <p className="text-foreground-500 text-xs leading-relaxed">
                    {member.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                社会声誉
              </h2>
              <p className="text-foreground-500 text-sm">
                来自学术界与文化爱好者的认可
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                  "黄游下戟的文献考据非常扎实，是近年来同类研究中难得一见的严谨之作。"
                </p>
                <p className="text-xs text-foreground-400">—— 某某大学中文系教授</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                  "这个平台让晦涩的古典文化变得通俗易懂，我已经推荐给了不少朋友。"
                </p>
                <p className="text-xs text-foreground-400">—— 文化爱好者 张先生</p>
              </div>
              <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-foreground-600 text-sm leading-relaxed mb-3">
                  "作为传统文化教育工作者，我很高兴看到这样专业而有温度的内容平台。"
                </p>
                <p className="text-xs text-foreground-400">—— 中学语文教师 李老师</p>
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm">
                如有任何问题或合作意向，欢迎随时与我们联系
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（限500字）"
                ></textarea>
              </div>

              <div className="honeypot-wrapper">
                <input
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 rounded-lg font-medium text-sm transition-colors whitespace-nowrap cursor-pointer"
              >
                {submitting ? '提交中...' : '发送消息'}
              </button>

              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 text-center">
                  感谢您的留言！我们会尽快与您联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 text-center">
                  {formError || '提交失败，请稍后重试'}
                </div>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}